import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import EquipamentoForm from '../components/EquipamentoForm';
import { useAuth } from '../contexts/AuthContext';
import * as authService from '../services/authService';
import {
  UserCircleIcon,
  BellIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  CogIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    professionalType: 'Medico', // Medico, Fonoaudiologo, Engenheiro
    // Médico
    crm: '',
    crmState: '',
    // Fonoaudiólogo
    crfa: '',
    crfaRegion: '',
    // Engenheiro
    crea: '',
    creaState: '',
    specialty: '',
    phone: '',
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    examAlerts: true,
    reportAlerts: false,
    systemUpdates: true,
  });

  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      
      // Buscar dados reais do usuário logado do backend
      const userData = await authService.getCurrentUser();
      
      setProfileData({
        name: userData.name || '',
        email: userData.email || '',
        professionalType: userData.professionalType || 'Medico',
        crm: userData.crm || '',
        crmState: userData.crmState || '',
        crfa: userData.crfa || '',
        crfaRegion: userData.crfaRegion || '',
        crea: userData.crea || '',
        creaState: userData.creaState || '',
        specialty: userData.specialization || '',
        phone: userData.phoneNumber || '',
      });
    } catch (err) {
      console.error('Erro ao carregar perfil:', err);
      toast.error('Erro ao carregar dados do perfil');
    } finally {
      setLoading(false);
    }
  };

  const professionalTypes = [
    { value: 'Medico', label: 'Médico (CRM)' },
    { value: 'Fonoaudiologo', label: 'Fonoaudiólogo (CRFa)' },
    { value: 'EngenheiroTrabalho', label: 'Engenheiro do Trabalho (CREA)' },
  ];

  const brStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const crfaRegions = [
    '1ª Região (SP, MS, MT)',
    '2ª Região (MG, ES)',
    '3ª Região (PR, SC, RS)',
    '4ª Região (PE, PB, RN, AL)',
    '5ª Região (BA, SE)',
    '6ª Região (DF, GO, TO, AM, RR, AC, RO, AP, PA)',
    '7ª Região (RJ, MA, PI, CE)',
  ];

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');

    const loadingToast = toast.loading('Salvando perfil...');

    try {
      // Preparar dados para enviar ao backend
      const updateData = {
        name: profileData.name,
        professionalType: profileData.professionalType,
        specialization: profileData.specialty,
        phoneNumber: profileData.phone,
      };

      // Adicionar campos específicos por tipo profissional
      if (profileData.professionalType === 'Medico') {
        updateData.crm = profileData.crm;
        updateData.crmState = profileData.crmState;
      } else if (profileData.professionalType === 'Fonoaudiologo') {
        updateData.crfa = profileData.crfa;
        updateData.crfaRegion = profileData.crfaRegion;
      } else if (profileData.professionalType === 'Engenheiro') {
        updateData.crea = profileData.crea;
        updateData.creaState = profileData.creaState;
      }

      // Atualizar perfil no backend
      const updatedUser = await authService.updateProfile(updateData);
      
      toast.dismiss(loadingToast);
      toast.success('Perfil atualizado com sucesso!', {
        icon: '✅',
        duration: 3000,
      });
      setSuccessMessage('Perfil atualizado com sucesso!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Recarregar dados do perfil
      await loadUserProfile();
    } catch (err) {
      console.error('Erro ao salvar perfil:', err);
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.message || 'Erro ao salvar perfil', {
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');

    try {
      // TODO: Implementar endpoint de notificações no backend quando necessário
      // Por enquanto, apenas salva no estado local
      toast.success('Preferências de notificação salvas!', {
        icon: '🔔',
        duration: 3000,
      });
      setSuccessMessage('Notificações atualizadas com sucesso!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');

    // Validações
    if (securityData.newPassword !== securityData.confirmPassword) {
      toast.error('As senhas não coincidem', {
        icon: '⚠️',
        duration: 4000,
      });
      setLoading(false);
      return;
    }

    if (securityData.newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres', {
        icon: '⚠️',
        duration: 4000,
      });
      setLoading(false);
      return;
    }

    const loadingToast = toast.loading('Alterando senha...');

    try {
      // Chamar endpoint de alteração de senha
      await authService.changePassword(
        securityData.currentPassword,
        securityData.newPassword,
        securityData.confirmPassword
      );
      
      toast.dismiss(loadingToast);
      toast.success('Senha alterada com sucesso!', {
        icon: '🔒',
        duration: 3000,
      });
      setSuccessMessage('Senha alterada com sucesso!');
      setSecurityData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.message || 'Erro ao alterar senha. Verifique a senha atual.', {
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Perfil', icon: UserCircleIcon },
    { id: 'equipments', name: 'Equipamentos', icon: CogIcon },
    { id: 'notifications', name: 'Notificações', icon: BellIcon },
    { id: 'security', name: 'Segurança', icon: ShieldCheckIcon },
  ];

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <CogIcon className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Configurações</h1>
            </div>
            <p className="text-gray-600">Gerencie suas preferências e informações da conta</p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
              ✓ {successMessage}
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      required
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Profissional *
                    </label>
                    <select
                      required
                      value={profileData.professionalType}
                      onChange={(e) => setProfileData({ ...profileData, professionalType: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      {professionalTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Campos para MÉDICO (CRM) */}
                  {profileData.professionalType === 'Medico' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Número CRM *
                        </label>
                        <input
                          type="text"
                          required
                          value={profileData.crm}
                          onChange={(e) => setProfileData({ ...profileData, crm: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="123456"
                          maxLength={6}
                        />
                        <p className="text-xs text-gray-500 mt-1">Ex: 123456</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          UF do CRM *
                        </label>
                        <select
                          required
                          value={profileData.crmState}
                          onChange={(e) => setProfileData({ ...profileData, crmState: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                          <option value="">Selecione</option>
                          {brStates.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Ex: SP, RJ, MG</p>
                      </div>
                    </div>
                  )}

                  {/* Campos para FONOAUDIÓLOGO (CRFa) */}
                  {profileData.professionalType === 'Fonoaudiologo' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Número CRFa *
                        </label>
                        <input
                          type="text"
                          required
                          value={profileData.crfa}
                          onChange={(e) => setProfileData({ ...profileData, crfa: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="12345"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ex: 12345</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Região CRFa *
                        </label>
                        <select
                          required
                          value={profileData.crfaRegion}
                          onChange={(e) => setProfileData({ ...profileData, crfaRegion: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                          <option value="">Selecione a região</option>
                          {crfaRegions.map((region) => (
                            <option key={region} value={region}>
                              {region}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Selecione sua região de atuação</p>
                      </div>
                    </div>
                  )}

                  {/* Campos para ENGENHEIRO DO TRABALHO (CREA) */}
                  {profileData.professionalType === 'EngenheiroTrabalho' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Número CREA *
                        </label>
                        <input
                          type="text"
                          required
                          value={profileData.crea}
                          onChange={(e) => setProfileData({ ...profileData, crea: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="5062345678"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ex: 5062345678</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          UF do CREA *
                        </label>
                        <select
                          required
                          value={profileData.creaState}
                          onChange={(e) => setProfileData({ ...profileData, creaState: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                          <option value="">Selecione</option>
                          {brStates.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Ex: SP, RJ, MG</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Especialização
                    </label>
                    <input
                      type="text"
                      value={profileData.specialty}
                      onChange={(e) => setProfileData({ ...profileData, specialty: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={
                        profileData.professionalType === 'Medico' ? 'Ex: Otorrinolaringologia' :
                        profileData.professionalType === 'Fonoaudiologo' ? 'Ex: Audiologia Clínica' :
                        'Ex: Segurança do Trabalho'
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(11) 98765-4321"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50"
                  >
                    {loading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </form>
              )}

              {/* Equipments Tab */}
              {activeTab === 'equipments' && (
                <EquipamentoForm />
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <form onSubmit={handleNotificationSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Notificações por E-mail</p>
                        <p className="text-sm text-gray-600">Receber atualizações por e-mail</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.emailNotifications}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              emailNotifications: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Alertas de Novos Exames</p>
                        <p className="text-sm text-gray-600">Notificar sobre novos exames cadastrados</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.examAlerts}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              examAlerts: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Alertas de Laudos</p>
                        <p className="text-sm text-gray-600">Notificar quando laudos estiverem prontos</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.reportAlerts}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              reportAlerts: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Atualizações do Sistema</p>
                        <p className="text-sm text-gray-600">Receber notificações sobre atualizações</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.systemUpdates}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              systemUpdates: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50"
                  >
                    {loading ? 'Salvando...' : 'Salvar Preferências'}
                  </button>
                </form>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <form onSubmit={handleSecuritySubmit} className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <KeyIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-900">Alteração de Senha</p>
                        <p className="text-sm text-yellow-800 mt-1">
                          Use uma senha forte com pelo menos 6 caracteres, incluindo letras e números.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Senha Atual *
                    </label>
                    <input
                      type="password"
                      required
                      value={securityData.currentPassword}
                      onChange={(e) => setSecurityData({ ...securityData, currentPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Digite sua senha atual"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nova Senha *
                    </label>
                    <input
                      type="password"
                      required
                      value={securityData.newPassword}
                      onChange={(e) => setSecurityData({ ...securityData, newPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Digite sua nova senha"
                      minLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Nova Senha *
                    </label>
                    <input
                      type="password"
                      required
                      value={securityData.confirmPassword}
                      onChange={(e) => setSecurityData({ ...securityData, confirmPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Confirme sua nova senha"
                      minLength={6}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50"
                  >
                    {loading ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
